// inventoryData.js
// All data logic for the Inventory View module: field mapping, formatting,
// Firestore services (cached/shared listeners), and the filter/sort composable.
// Keeping this separate from InventoryView.vue so logic isn't mixed with markup.

import { ref, computed, watch } from 'vue'
import { collection, onSnapshot } from 'firebase/firestore'
import { getFirestore } from "firebase/firestore";
import { firebaseApp } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);// <-- update to your existing firebase.js export

// ---------------------------------------------------------------------------
// Field mapping — only place raw Firestore field names should appear.
// ---------------------------------------------------------------------------

const INVENTORY_FIELDS = {
  itemCode: 'Simple Code',
  description: 'Description',
  description2: 'Description 2',
  itemGroup: 'Group',
  itemCategory: 'Item Category', // NEW FIELD MAPPING
  qtyAvailable: 'Qty Available',
  qtyOnHand: 'Qty On Hand',
  qtyOnSalesOrder: 'Qty On S/Order',
  qtyOnPurchaseOrder: 'Qty On P/Order',
  reservedQty: 'Reserved Qty',
  minimumLevel: 'Minimum Level',
  maximumLevel: 'Max Level',
  averageUnitCost: 'Average Unit Cost',
  defaultExclPrice: 'Default Excl. Price',
  isDeleted: 'IsDeleted',
}

const BOM_FIELDS = {
  billCode: 'BillCode',
  billDescription: 'BillDescription',
  components: 'Components',
  componentItemCode: 'ItemCode',
  componentDescription: 'ComponentDescription',
  qtyRequired: 'QtyRequired',
  isDeleted: 'IsDeleted',
}

function toNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function toText(value) {
  if (value === null || value === undefined || value === '') return '-'
  return String(value).trim()
}

function normalizeCode(code) {
  return (code ?? '').toString().trim().toUpperCase()
}

function mapInventoryDoc(doc, id) {
  const f = INVENTORY_FIELDS
  return {
    id,
    itemCode: toText(doc[f.itemCode]),
    itemCodeKey: normalizeCode(doc[f.itemCode]),
    description: toText(doc[f.description]),
    description2: toText(doc[f.description2]),
    itemGroup: toText(doc[f.itemGroup]),
    itemCategory: toText(doc[f.itemCategory]), // NEW MAPPED DATA
    qtyAvailable: toNumber(doc[f.qtyAvailable]),
    qtyOnHand: toNumber(doc[f.qtyOnHand]),
    qtyOnSalesOrder: toNumber(doc[f.qtyOnSalesOrder]),
    qtyOnPurchaseOrder: toNumber(doc[f.qtyOnPurchaseOrder]),
    reservedQty: toNumber(doc[f.reservedQty]),
    minimumLevel: toNumber(doc[f.minimumLevel]),
    maximumLevel: toNumber(doc[f.maximumLevel]),
    averageUnitCost: toNumber(doc[f.averageUnitCost]),
    defaultExclPrice: toNumber(doc[f.defaultExclPrice]),
    isDeleted: Boolean(doc[f.isDeleted]),
    hasBom: false,
  }
}

function mapBomDoc(doc, id) {
  const f = BOM_FIELDS
  const components = Array.isArray(doc[f.components]) ? doc[f.components] : []
  return {
    id,
    billCode: toText(doc[f.billCode]),
    billCodeKey: normalizeCode(doc[f.billCode]),
    billDescription: toText(doc[f.billDescription]),
    isDeleted: Boolean(doc[f.isDeleted]),
    components: components
      .filter((c) => c && c[f.componentItemCode])
      .map((c) => ({
        componentCode: toText(c[f.componentItemCode]),
        componentCodeKey: normalizeCode(c[f.componentItemCode]),
        componentDescription: toText(c[f.componentDescription]),
        qtyRequired: toNumber(c[f.qtyRequired]),
      })),
  }
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const currencyFormatter = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const numberFormatter = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 2 })

export function formatCurrency(value) {
  const n = Number(value)
  return currencyFormatter.format(Number.isFinite(n) ? n : 0)
}

export function formatQty(value) {
  const n = Number(value)
  return numberFormatter.format(Number.isFinite(n) ? n : 0)
}

// ---------------------------------------------------------------------------
// Inventory service — one shared cached Firestore listener for the whole app.
// ---------------------------------------------------------------------------

const INVENTORY_COLLECTION = 'Evolution Inventory'
let invItems = []
let invByCode = new Map()
let invUnsub = null
let invListenerCount = 0
let invLastRefreshed = null
const invListeners = new Set()

function invNotify() {
  for (const cb of invListeners) cb({ items: invItems, lastRefreshed: invLastRefreshed })
}

function invStart() {
  if (invUnsub) return
  invUnsub = onSnapshot(
    collection(db, INVENTORY_COLLECTION),
    (snapshot) => {
      const mapped = []
      const byCode = new Map()
      snapshot.forEach((doc) => {
        const item = mapInventoryDoc(doc.data(), doc.id)
        if (item.isDeleted) return
        mapped.push(item)
        byCode.set(item.itemCodeKey, item)
      })
      invItems = mapped
      invByCode = byCode
      invLastRefreshed = new Date()
      invNotify()
    },
    (error) => {
      console.error('[inventoryData] inventory listener error:', error)
      invNotify()
    }
  )
}

export function subscribeInventory(callback) {
  invListenerCount += 1
  invStart()
  invListeners.add(callback)
  callback({ items: invItems, lastRefreshed: invLastRefreshed })
  return () => {
    invListeners.delete(callback)
    invListenerCount -= 1
    if (invListenerCount <= 0 && invUnsub) {
      invUnsub()
      invUnsub = null
    }
  }
}

function getInventoryItemByCode(code) {
  if (!code) return null
  return invByCode.get(code.toString().trim().toUpperCase()) || null
}

// ---------------------------------------------------------------------------
// BOM service — one shared cached Firestore listener, resolved against the
// inventory cache above (no per-row reads).
// ---------------------------------------------------------------------------

const BOM_COLLECTION = 'BOMRecipes'
let bomByParentCode = new Map()
let bomUnsub = null
let bomListenerCount = 0
let bomLoaded = false
const bomListeners = new Set()

function bomNotify() {
  for (const cb of bomListeners) cb({ bomByParentCode, loaded: bomLoaded })
}

function bomStart() {
  if (bomUnsub) return
  bomUnsub = onSnapshot(
    collection(db, BOM_COLLECTION),
    (snapshot) => {
      const map = new Map()
      snapshot.forEach((doc) => {
        const bom = mapBomDoc(doc.data(), doc.id)
        if (bom.isDeleted || !bom.components.length) return
        map.set(bom.billCodeKey, bom)
      })
      bomByParentCode = map
      bomLoaded = true
      bomNotify()
    },
    (error) => {
      console.error('[inventoryData] BOM listener error:', error)
      bomLoaded = true
      bomNotify()
    }
  )
}

export function subscribeBom(callback) {
  bomListenerCount += 1
  bomStart()
  bomListeners.add(callback)
  callback({ bomByParentCode, loaded: bomLoaded })
  return () => {
    bomListeners.delete(callback)
    bomListenerCount -= 1
    if (bomListenerCount <= 0 && bomUnsub) {
      bomUnsub()
      bomUnsub = null
    }
  }
}

export function hasBom(itemCodeKey) {
  return bomByParentCode.has(itemCodeKey)
}

function resolveComponentAvailability(component) {
  const inventoryItem = getInventoryItemByCode(component.componentCodeKey)
  if (!inventoryItem) {
    return { ...component, qtyAvailable: 0, qtyOnPurchaseOrder: 0, inventoryRecordFound: false, availabilityStatus: 'missing', buildableQty: null }
  }
  const qtyAvailable = inventoryItem.qtyAvailable
  const qtyOnPurchaseOrder = inventoryItem.qtyOnPurchaseOrder
  let status = 'available'
  if (qtyAvailable <= 0) status = 'out_of_stock'
  else if (component.qtyRequired > 0 && qtyAvailable < component.qtyRequired) status = 'insufficient'
  const buildableQty = component.qtyRequired > 0 ? Math.floor(qtyAvailable / component.qtyRequired) : null
  return { ...component, qtyAvailable, qtyOnPurchaseOrder, inventoryRecordFound: true, availabilityStatus: status, buildableQty }
}

export function resolveBomForItem(itemCodeKey) {
  const bom = bomByParentCode.get(itemCodeKey)
  if (!bom) return null
  const components = bom.components.map(resolveComponentAvailability)
  const validBuildable = components.filter((c) => c.inventoryRecordFound && c.buildableQty !== null).map((c) => c.buildableQty)
  const hasMissing = components.some((c) => !c.inventoryRecordFound)
  const estimatedBuildableQty = hasMissing || validBuildable.length === 0 ? null : Math.min(...validBuildable)
  return { billCode: bom.billCode, billDescription: bom.billDescription, components, estimatedBuildableQty }
}

// ---------------------------------------------------------------------------
// Filter / search / sort / pagination composable
// ---------------------------------------------------------------------------

export const PAGE_SIZES = [50, 100, 250]

export function useInventoryFilters(itemsRef) {
  const searchTerm = ref('')
  const debouncedSearch = ref('')
  let debounceHandle = null

  watch(searchTerm, (val) => {
    clearTimeout(debounceHandle)
    debounceHandle = setTimeout(() => {
      debouncedSearch.value = val.trim().toLowerCase().replace(/\s+/g, ' ')
    }, 250)
  })

  const filters = ref({
    itemCode: '',
    description: '',
    itemGroup: '',
    bom: 'all',
    qtyAvailable: { op: 'none', value: '', value2: '' },
  })

  const sort = ref({ key: 'itemCode', dir: 'asc' })
  const page = ref(1)
  const pageSize = ref(PAGE_SIZES[0])

  function resetFilters() {
    searchTerm.value = ''
    debouncedSearch.value = ''
    filters.value = {
      itemCode: '',
      description: '',
      itemGroup: '',
      bom: 'all',
      qtyAvailable: { op: 'none', value: '', value2: '' },
    }
    page.value = 1
  }

  function matchesText(itemText, filterText) {
    if (!filterText) return true
    return itemText.toLowerCase().includes(filterText.toLowerCase())
  }

  function matchesNumeric(value, rule) {
    if (!rule || rule.op === 'none') return true
    const v = Number(value) || 0
    switch (rule.op) {
      case 'equals': return v === Number(rule.value)
      case 'gt': return v > Number(rule.value)
      case 'lt': return v < Number(rule.value)
      case 'between': return v >= Number(rule.value) && v <= Number(rule.value2)
      case 'zero': return v === 0
      case 'gt_zero': return v > 0
      default: return true
    }
  }

  const filtered = computed(() => {
    const term = debouncedSearch.value
    return itemsRef.value.filter((item) => {
      if (term) {
        // ADDED itemCategory to global search
        const haystack = [item.itemCode, item.description, item.description2, item.itemGroup, item.itemCategory].join(' ').toLowerCase()
        if (!haystack.includes(term)) return false
      }
      if (!matchesText(item.itemCode, filters.value.itemCode)) return false
      if (!matchesText(item.description, filters.value.description) && !matchesText(item.description2, filters.value.description)) return false
      if (!matchesText(item.itemGroup, filters.value.itemGroup)) return false
      if (filters.value.bom === 'bom' && !item.hasBom) return false
      if (filters.value.bom === 'non_bom' && item.hasBom) return false
      if (!matchesNumeric(item.qtyAvailable, filters.value.qtyAvailable)) return false
      return true
    })
  })

  const sorted = computed(() => {
    const list = [...filtered.value]
    const { key, dir } = sort.value
    const factor = dir === 'asc' ? 1 : -1
    list.sort((a, b) => {
      const av = a[key]
      const bv = b[key]
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * factor
    })
    return list
  })

  const totalFiltered = computed(() => sorted.value.length)
  const totalPages = computed(() => Math.max(1, Math.ceil(totalFiltered.value / pageSize.value)))
  const paged = computed(() => {
    const start = (page.value - 1) * pageSize.value
    return sorted.value.slice(start, start + pageSize.value)
  })

  watch([filtered, pageSize], () => {
    if (page.value > totalPages.value) page.value = 1
  })

  function setSort(key) {
    if (sort.value.key === key) sort.value.dir = sort.value.dir === 'asc' ? 'desc' : 'asc'
    else sort.value = { key, dir: 'asc' }
  }

  return { searchTerm, filters, sort, setSort, page, pageSize, totalFiltered, totalPages, paged, resetFilters }
}